import { useState } from "react";
import { useAuth, defaultRoute } from "@/lib/auth";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, Lock, KeyRound } from "lucide-react";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { user, login, setupPassword, loading } = useAuth();
  const [step, setStep] = useState("email"); // email | password | setup
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [checking, setChecking] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to={defaultRoute(user)} replace />;

  const cleanEmail = () => email.trim().toLowerCase().replace(/\s+/g, "");

  const onCheckEmail = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      const { data } = await axios.post(`${API_ROOT}/auth/check-email`, { email: cleanEmail() });
      setName(data.name || "");
      if (data.status === "setup_required") setStep("setup");
      else setStep("password");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not check email — try again");
    } finally { setChecking(false); }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    try {
      const u = await login(cleanEmail(), password.trim());
      toast.success("Welcome back");
      nav(defaultRoute(u));
    } catch (err) {
      if (err?.response?.status === 409) { setPassword(""); setStep("setup"); return; }
      toast.error(err?.response?.data?.detail || "Authentication failed");
    }
  };

  const backToEmail = () => { setStep("email"); setPassword(""); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left form panel */}
      <div className="flex items-center justify-center px-6 py-10 lg:px-16 bg-white relative">
        <div className="absolute top-6 left-6 lg:left-16 flex items-center gap-3">
          <img src="/ccfi-logo.png?v=4" alt="CCFI" className="w-16 h-16 object-contain" />
          <div className="leading-tight">
            <div className="font-display font-bold text-slate-900 text-base">CCFI <span className="text-cyan-600">Compass</span></div>
            <div className="text-[10px] text-slate-500 stamp-label mt-0.5">Operations Suite</div>
          </div>
        </div>

        <div className="w-full max-w-sm">
          {step === "email" && (
            <>
              <div className="stamp-label text-cyan-600 mb-3">Sign In</div>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Welcome back.</h1>
              <p className="text-sm text-slate-500 mt-2">Enter your staff email to continue.</p>
              <form onSubmit={onCheckEmail} className="mt-8 space-y-4" data-testid="auth-form">
                <div>
                  <Label className="stamp-label text-slate-700">Email</Label>
                  <Input data-testid="email-input" type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus className="mt-1.5 h-11 rounded-sm" placeholder="you@ccfencingindustries.com.au" />
                </div>
                <Button data-testid="email-continue-btn" type="submit" disabled={checking} className="w-full h-11 rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white font-semibold tracking-tight">
                  {checking ? <Loader2 className="animate-spin" size={18} /> : (<>Continue <ArrowRight size={16} className="ml-1" /></>)}
                </Button>
              </form>
            </>
          )}

          {step === "password" && (
            <>
              <div className="stamp-label text-cyan-600 mb-3">Sign In</div>
              <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">G&apos;day{name ? `, ${name.split(" ")[0]}` : ""}.</h1>
              <p className="text-sm text-slate-500 mt-2 font-mono">{cleanEmail()}</p>
              <form onSubmit={onLogin} className="mt-8 space-y-4" data-testid="auth-form">
                <div>
                  <Label className="stamp-label text-slate-700">Password</Label>
                  <Input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus className="mt-1.5 h-11 rounded-sm" autoComplete="current-password" />
                </div>
                <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full h-11 rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white font-semibold tracking-tight">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : (<>Sign in <ArrowRight size={16} className="ml-1" /></>)}
                </Button>
                <button type="button" onClick={backToEmail} className="text-xs text-slate-500 hover:text-cyan-600 flex items-center gap-1" data-testid="back-to-email-btn">
                  <ArrowLeft size={12} /> Use a different email
                </button>
              </form>
            </>
          )}

          {step === "setup" && (
            <>
              <div className="stamp-label text-emerald-600 mb-3 flex items-center gap-1.5"><KeyRound size={12} /> Account not activated</div>
              <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">Almost there.</h1>
              <div className="mt-6 border border-emerald-200 bg-emerald-50 rounded-sm p-4 flex items-start gap-3" data-testid="setup-needs-link-box">
                <KeyRound size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <span className="font-mono text-xs">{cleanEmail()}</span> is on the team list but hasn&apos;t been activated yet.<br />
                  <span className="text-xs text-emerald-800">Ask your administrator for your one-time <b>setup link</b> — open it to create your own password.</span>
                </div>
              </div>
              <Button onClick={backToEmail} variant="outline" className="mt-4 rounded-sm h-11" data-testid="back-to-email-btn">
                <ArrowLeft size={15} className="mr-1.5" /> Try a different email
              </Button>
            </>
          )}

          <div className="mt-6 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="private-app-notice">
            <Lock size={14} className="text-slate-400 mt-0.5 shrink-0" />
            <span>Private staff system — access is by invitation only. First time here? Enter your email and you&apos;ll be asked to create your own password.</span>
          </div>
        </div>
      </div>

      {/* Right industrial visual */}
      <div className="hidden lg:flex relative bg-slate-900 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1455165814004-1126a7199f9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwc3RlZWwlMjBmYWJyaWNhdGlvbiUyMHdlbGRpbmd8ZW58MHx8fHwxNzgyNjA2NDM0fDA&ixlib=rb-4.1.0&q=85"
          alt="Steel fabrication"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-900/40 to-cyan-900/40" />
        <div className="absolute inset-0 bg-hatch opacity-30" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white w-full">
          <div className="stamp-label text-cyan-400 mb-3">EST. CENTRAL COAST NSW</div>
          <h2 className="font-display text-5xl xl:text-6xl font-bold tracking-tight leading-[0.95] max-w-md">
            Built like the<br/>
            fences we<br/>
            install.<span className="text-cyan-500">_</span>
          </h2>
          <p className="text-slate-300 mt-6 max-w-md text-sm leading-relaxed">
            End-to-end operations for enquiries, quoting, pipeline &amp; installs — purpose-built for steel fabrication &amp; fencing contractors.
          </p>
          <div className="mt-10 flex items-center gap-6 text-xs text-slate-400">
            <div><div className="font-display text-2xl text-white font-semibold">25<span className="text-cyan-500">+</span></div><div className="stamp-label">Years</div></div>
            <div><div className="font-display text-2xl text-white font-semibold">8</div><div className="stamp-label">Pipeline Stages</div></div>
            <div><div className="font-display text-2xl text-white font-semibold">∞</div><div className="stamp-label">Quotes</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
